import { Link } from 'react-router-dom';
import { ArrowRight, Grid3X3, Shield, Zap, Layers, Settings, Users } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LandingPage() {
    return (
        <div className="min-h-screen bg-white" data-testid="landing-page">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                                <Grid3X3 className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-slate-900 tracking-tight">AppShell</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <Link to="/login">
                                <Button variant="ghost" className="text-slate-600 hover:text-slate-900" data-testid="nav-login-btn">
                                    Sign in
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="nav-register-btn">
                                    Get Started
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="landing-hero pt-32 pb-24 px-6 md:px-12">
                <div className="max-w-7xl mx-auto">
                    <div className="max-w-3xl mx-auto text-center animate-slide-up">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full text-indigo-700 text-sm font-medium mb-8">
                            <Zap className="w-4 h-4" />
                            Modular Platform Architecture
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6">
                            One Platform.<br />
                            <span className="text-indigo-600">Unlimited Apps.</span>
                        </h1>
                        <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-10 max-w-2xl mx-auto">
                            A unified shell that hosts multiple independent applications under a single domain. 
                            Build, deploy, and manage your app ecosystem with ease.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link to="/login">
                                <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base" data-testid="hero-cta-btn">
                                    Start Building
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button size="lg" variant="outline" className="border-slate-200 text-slate-700 px-8 py-6 text-base" data-testid="hero-login-btn">
                                    Sign In to Dashboard
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {/* Platform Preview */}
                    <div className="mt-20 animate-slide-up stagger-2">
                        <div className="relative max-w-5xl mx-auto">
                            <div className="bg-slate-900 rounded-t-xl p-3 flex items-center gap-2">
                                <div className="flex gap-1.5">
                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                </div>
                                <div className="flex-1 text-center">
                                    <span className="text-slate-400 text-sm">platform.appshell.io/dashboard</span>
                                </div>
                            </div>
                            <div className="bg-slate-50 rounded-b-xl border border-slate-200 p-8 shadow-2xl">
                                <div className="grid grid-cols-3 gap-4">
                                    {[
                                        { name: 'Analytics', status: 'live', color: '#4F46E5' },
                                        { name: 'Projects', status: 'beta', color: '#10B981' },
                                        { name: 'CRM', status: 'live', color: '#F59E0B' }
                                    ].map((app, i) => (
                                        <div key={i} className="bg-white rounded-lg p-4 border border-slate-200 relative overflow-hidden">
                                            <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: app.color }}></div>
                                            <div className="w-10 h-10 rounded-lg mb-3" style={{ backgroundColor: `${app.color}20` }}>
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Grid3X3 className="w-5 h-5" style={{ color: app.color }} />
                                                </div>
                                            </div>
                                            <p className="font-semibold text-slate-900 text-sm">{app.name}</p>
                                            <span className={`inline-block mt-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                                                app.status === 'live' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'
                                            }`}>
                                                {app.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-24 px-6 md:px-12 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-semibold text-slate-900 tracking-tight mb-4">
                            Built for Modularity
                        </h2>
                        <p className="text-base text-slate-600 max-w-2xl mx-auto">
                            Everything you need to build and manage a multi-app platform
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: Layers,
                                title: 'Modular Architecture',
                                description: 'Each app lives independently with its own components, routes, and logic while sharing the platform shell.'
                            },
                            {
                                icon: Shield,
                                title: 'Unified Authentication',
                                description: 'Single sign-on across all apps with flexible auth options for apps that need their own login systems.'
                            },
                            {
                                icon: Settings,
                                title: 'App Registry',
                                description: 'Centralized management of all apps with status controls, permissions, and visibility settings.'
                            },
                            {
                                icon: Grid3X3,
                                title: 'Single Domain',
                                description: 'All apps accessible under one domain. Users experience a cohesive platform, not separate websites.'
                            },
                            {
                                icon: Zap,
                                title: 'Instant Deployment',
                                description: 'Add new apps with minimal configuration. Just register in the app registry and define routes.'
                            },
                            {
                                icon: Users,
                                title: 'Team Ready',
                                description: 'Different teams can work on different apps independently without affecting the platform.'
                            }
                        ].map((feature, i) => (
                            <div key={i} className="feature-card animate-slide-up" style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                                    <feature.icon className="w-6 h-6 text-indigo-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6 md:px-12">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl font-semibold text-slate-900 tracking-tight mb-4">
                        Ready to Build Your Platform?
                    </h2>
                    <p className="text-base text-slate-600 mb-8">
                        Start with the shell and add apps as you grow. Scale from one to hundreds.
                    </p>
                    <Link to="/login">
                        <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base" data-testid="cta-register-btn">
                            Get Started Free
                            <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-6 md:px-12 border-t border-slate-200">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center">
                            <Grid3X3 className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-sm font-semibold text-slate-900">AppShell</span>
                    </div>
                    <p className="text-sm text-slate-500">
                        &copy; {new Date().getFullYear()} AppShell. Modular Platform Architecture.
                    </p>
                </div>
            </footer>
        </div>
    );
}
